import * as React from 'react';
import Collapse from '@mui/material/Collapse';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { OrderProduct, ResponseOrderDto } from '../types/dtos';
import { Stack, styled } from '@mui/material';
import { t } from 'i18next';

type DetailsViewProps = {
    order: ResponseOrderDto;
    isAdmin: boolean;
    handleBack?: () => void;
    columnNames: string[];
};
type RowProps = {
    product: OrderProduct;
    columnNames: string[];
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.common.black,
        fontWeight: 'bold',
        width: '25%',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));
const DropStyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.action.active,
        color: theme.palette.common.white,
        fontWeight: 'bold',
        width: '25%',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

function Row({ product, columnNames }: RowProps) {
    columnNames[0] = t('ORDER_VIEW.SIDE_DISH');
    const [open, setOpen] = React.useState(false);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset', cursor: 'pointer' } }} onClick={() => setOpen(!open)}>
                <TableCell align="left" component="th" scope="row">
                    {product.name}
                </TableCell>
                <TableCell align="center">{product.quantity}</TableCell>
                <TableCell align="center">{product.priceAtOrder}</TableCell>
                <TableCell align="right">
                    {Number(product.priceAtOrder) * product.quantity +
                        product.nestedProducts.reduce((total, item) => total + Number(item.price), 0) +
                        ' RSD'}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell padding="none" colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit style={{ padding: 0, margin: 0 }}>
                        <Table component={Paper} size="small" aria-label="purchases">
                            <TableHead>
                                <TableRow>
                                    {columnNames.map((columnName, index) => (
                                        <DropStyledTableCell
                                            key={index}
                                            align={
                                                index === 0
                                                    ? 'left'
                                                    : index === columnNames.length - 1
                                                      ? 'right'
                                                      : 'center'
                                            }
                                        >
                                            {columnName}
                                        </DropStyledTableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {product.nestedProducts.map((nestedProducts) => (
                                    <TableRow key={nestedProducts.id}>
                                        <TableCell component="th" scope="row">
                                            {nestedProducts.name}
                                        </TableCell>
                                        <TableCell align="center">{1}</TableCell>
                                        <TableCell align="center">{nestedProducts.price}</TableCell>
                                        <TableCell align="right">{Number(nestedProducts.price) * 1}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

export default function CollapsibleTable({ order, columnNames }: DetailsViewProps) {
    return (
        <>
            <Stack sx={{ flex: 1, marginBottom: 10, flexDirection: { xs: 'column', sm: 'column', md: 'row' } }}>
                <Stack sx={{ flex: 6, direction: 'column' }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 700 }} aria-label="customized table">
                            <TableHead>
                                <TableRow>
                                    {columnNames.map((columnName, index) => (
                                        <StyledTableCell
                                            key={index}
                                            align={
                                                index === 0
                                                    ? 'left'
                                                    : index === columnNames.length - 1
                                                      ? 'right'
                                                      : 'center'
                                            }
                                        >
                                            {columnName}
                                        </StyledTableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {order.products.map((product, index) => (
                                    <Row key={index} product={product} columnNames={columnNames} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            </Stack>
        </>
    );
}
